/* eslint-disable prefer-const */
import type { Config } from "tailwindcss"
import typographyModule from "@tailwindcss/typography"
import tailwindAnimateModule from "tailwindcss-animate"
import tailwindColors from "tailwindcss/colors"
// @ts-expect-error ts stuff
import flattenColorPalette from "tailwindcss/lib/util/flattenColorPalette"

interface TailwindPlugin {
	handler?: unknown
}

// Node's own require, not the config loader's: jiti and Turbopack both hand back
// an empty module for the typography plugin, and only a fresh resolve escapes
// their registry. Reached through process.getBuiltinModule so the dashboard,
// which imports this config in the browser for its colors, never sees a
// node:module import.
const requirePlugin = (name: string): unknown => {
	try {
		const builtin = (
			process as unknown as {
				getBuiltinModule?: (id: string) => {
					createRequire?: (path: string) => NodeJS.Require
				}
			}
		).getBuiltinModule?.("module")
		// The app declares this package, and this package declares the plugins.
		const ownManifest = builtin
			?.createRequire?.(`${process.cwd()}/index.js`)
			.resolve("@productlane/tailwind-config/package.json")
		return ownManifest && builtin?.createRequire?.(ownManifest)(name)
	} catch {
		return undefined
	}
}

const isPlugin = (value: unknown): boolean =>
	typeof value === "function" ||
	typeof (value as TailwindPlugin)?.handler === "function"

// Loaders wrap a CJS export at different depths (the export itself, `default`,
// or `default.default`), so unwrap until the plugin shows up.
const unwrapPlugin = (module: unknown): unknown => {
	let value = module
	for (let depth = 0; depth < 3; depth++) {
		if (isPlugin(value)) return value
		const next = (value as { default?: unknown })?.default
		if (next === undefined) break
		value = next
	}
	return value
}

// Both plugins ship as CJS. A config loader that hands back the module
// namespace instead of the export leaves Tailwind with an undefined handler,
// which it skips in silence, so the whole layer disappears from the bundle.
// Turbopack's loader hands back an empty object here, so fall back to a plain
// require, which resolves the real export.
export const resolvePlugin = <T>(module: T, name: string): T => {
	const unwrapped = unwrapPlugin(module)
	if (isPlugin(unwrapped)) return unwrapped as T

	const required = unwrapPlugin(requirePlugin(name))
	if (isPlugin(required)) return required as T

	throw new Error(`Tailwind plugin "${name}" did not resolve to a plugin`)
}

const typography = resolvePlugin(typographyModule, "@tailwindcss/typography")
const tailwindAnimate = resolvePlugin(
	tailwindAnimateModule,
	"tailwindcss-animate",
)

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function addVariablesForColors({ addBase, theme }: any) {
	// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
	let allColors = flattenColorPalette(theme("colors"))
	let newVars = Object.fromEntries(
		// eslint-disable-next-line @typescript-eslint/no-unsafe-argument
		Object.entries(allColors).map(([key, val]) => [`--${key}`, val]),
	)

	// eslint-disable-next-line @typescript-eslint/no-unsafe-call
	addBase({
		":root": newVars,
	})
}

export default {
	content: [""],
	theme: {
		fontSize: {
			xxs: ["0.6875rem", "1.5"],
			xs: ["0.75rem", "1.5"],
			sm: ["0.8125rem", "1.5"],
			base: ["0.9375rem", "1.5"],
			lg: ["1rem", "1.5"],
			xl: ["1.125rem", "1.3"],
			"2xl": ["1.25rem", "1.3"],
			"3xl": ["1.5rem", "1.25"],
			"4xl": ["2.441rem", "1.15"],
			"5xl": ["3.052rem", "1.1"],
			"6xl": ["4rem", "1.05"],
		},
		colors: {
			current: tailwindColors.current,
			transparent: tailwindColors.transparent,
			black: tailwindColors.black,
			red: tailwindColors.rose,
			white: tailwindColors.white,
			brand: tailwindColors.neutral,
			plane: "var(--text-green-500)",
			gray: {
				25: "#FCFCFD",
				50: "#F9FAFB",
				100: "#F4F5F5",
				150: "#EDEDED",
				200: "#E5E5E5",
				300: "#D0D1D2",
				350: "#CCD4E0",
				400: "#9FA7B7",
				450: "#7A7E85",
				500: "#707476",
				600: "#4E5155",
				700: "#303031",
				750: "#29292E",
				800: "#1A1A1E",
				850: "#151518",
				900: "#0F0F12",
				950: "#090A0B",
			},
			green: {
				50: "#F2FAF6",
				100: "#E3F4EB",
				200: "#C7E8D6",
				300: "#9AD6B7",
				400: "#6EC499",
				500: "#4CB782",
				600: "#3A986C",
				700: "#2B7857",
				800: "#235F47",
				900: "#1F4D3C",
				950: "#122E25",
			},
			amber: tailwindColors.amber,
			yellow: tailwindColors.yellow,
			purple: {
				...tailwindColors.violet,
			},
			blue: tailwindColors.blue,
			inherit: tailwindColors.inherit,
			violet: tailwindColors.violet,
		},
		extend: {
			fontWeight: {
				bold: "600",
				extrabold: "600",
				black: "600",
				normal: "450",
			},
			boxShadow: {
				button: "0px 1px 1px rgba(0, 0, 0, 0.06)",
				card: "0px 0px 0px 0.5px rgba(0, 0, 0, 0.09), 0px 3px 6px -2px rgba(0, 0, 0, 0.02), 0px 1px 1px 0px rgba(0, 0, 0, 0.04)",
				"card-dark":
					"0px 0px 0px 0.5px rgba(255, 255, 255, 0.09), 0px 3px 6px -2px rgba(0, 0, 0, 0.3), 0px 1px 1px 0px rgba(0, 0, 0, 0.2)",
				wider: "0px 16px 70px rgba(0, 0, 0, 0.07)",
				dark: "0px 7px 24px rgba(0, 0, 0, 0.55)",
				kbar: "0px 36px 70px rgba(0,0,0,0.26)",
				left: "-40px 0px 300px 20px",
				inner: "inset 0 1px 2px rgba(0, 0, 0, 0.05)",
			},
			fontFamily: {
				sans: ["InterVariable", "var(--font-inter)"],
			},
			borderWidth: {
				DEFAULT: ".5px",
			},
			ringWidth: {
				"1": "0.5px",
			},
			textColor: {
				primary: "var(--text-primary)",
				secondary: "var(--text-secondary)",
				tertiary: "var(--text-tertiary)",
			},
			stroke: {
				primary: "var(--text-primary)",
				secondary: "var(--text-secondary)",
				tertiary: "var(--text-tertiary)",
				test: "red",
				white: "white",
				strokeWidth: {
					4: "12px",
				},
			},
			letterSpacing: {
				tighter: "-0.02em",
				tight: "-0.01em",
				normal: "0em",
				wide: "0.025em",
				wider: "0.05em",
				widest: "0.1em",
			},
			gridTemplateColumns: {
				14: "repeat(14, minmax(0, 1fr))",
			},
			backgroundImage: {
				"gradient-radial": "radial-gradient(var(--tw-gradient-stops))",
			},
			maxWidth: {
				xxs: "288px",
			},
			zIndex: {
				1: "1",
			},
			gap: {
				inherit: "inherit",
			},
			transitionDelay: {
				400: "400ms",
				600: "600ms",
				800: "800ms",
			},
			animation: {
				"shape-stagger": "shape-stagger 0.8s ease-out",
				"timeline-box": "timeline-box 5s linear",
				"fade-in-text":
					"fade-in-text 1000ms ease 0s 1 normal backwards running",
				"arrow-fade-in": "arrow-fade-in 1s ease-out",
				"accordion-down": "accordion-down 0.15s ease-in-out",
				"accordion-up": "accordion-up 0.15s ease-in-out",
				"collapsible-down": "collapsible-down 0.2s ease-out",
				"collapsible-up": "collapsible-up 0.15s ease-out",
				"fade-in": "fade-in 0.2s ease-out",
			},
			transitionTimingFunction: {
				// curves by Benjamin De Cock: https://www.notion.so/productlane/Animations-908a29c221f14038b47e9d270a18ff85
				// using quad as the default, ordered from slow to fast
				out: "cubic-bezier(.25, .46, .45, .94)",
				"out-quad": "cubic-bezier(.25, .46, .45, .94)",
				"out-cubic": "cubic-bezier(.215, .61, .355, 1)",
				"out-quart": "cubic-bezier(.165, .84, .44, 1)",
				"out-quint": "cubic-bezier(.23, 1, .32, 1)",
				"out-expo": "cubic-bezier(.19, 1, .22, 1)",
				"out-circ": "cubic-bezier(.075, .82, .165, 1)",

				"in-expo": "cubic-bezier(0.95, 0.05, 0.795, 0.035)",

				// using quad as the default, ordered from slow to fast
				"in-out": "cubic-bezier(.455, .03, .515, .955)",
				"in-out-quad": "cubic-bezier(.455, .03, .515, .955)",
				"in-out-cubic": "cubic-bezier(.645, .045, .355, 1)",
				"in-out-quart": "cubic-bezier(.77, 0, .175, 1)",
				"in-out-quint": "cubic-bezier(.86, 0, .07, 1)",
				"in-out-expo": "cubic-bezier(1, 0, 0, 1)",
				"in-out-circ": "cubic-bezier(.785, .135, .15, .86)",
			},
			keyframes: {
				"fade-in": {
					"0%": { opacity: "0" },
					"100%": { opacity: "1" },
				},
				"arrow-fade-in": {
					"0%": { opacity: "0", transform: "scale(90%)" },
					"100%": { opacity: "1", transform: "scale(100%)" },
				},
				"shape-stagger": {
					"0%": { transform: "scaleY(0%)", fillOpacity: "0" },
					"100%": { transform: "scaleY(100%)", fillOpacity: "1" },
				},
				"timeline-box": {
					"0%": { opacity: "0", transform: "translateY(0rem)" },
					"16.6%": { opacity: "1", transform: "translateY(24rem)" },
					"33.2%": { opacity: "0", transform: "translateY(48rem)" },
					"49.8%": { opacity: "1", transform: "translateY(72rem)" },
					"66.4%": { opacity: "0", transform: "translateY(96rem)" },
					"85.4%": { opacity: "1", transform: "translateY(120rem)" },
					"100%": { opacity: "0", transform: "translateY(145rem)" },
				},
				"fade-in-text": {
					"0%": { opacity: "0", transform: "translateY(-10px)" },
					"100%": { opacity: "1", transform: "translateY(0px)" },
				},
				"accordion-down": {
					from: { height: "0" },
					to: { height: "var(--radix-accordion-content-height)" },
				},
				"accordion-up": {
					from: { height: "var(--radix-accordion-content-height)" },
					to: { height: "0" },
				},
				"collapsible-up": {
					from: { height: "var(--radix-collapsible-content-height)" },
					to: { height: "0" },
				},
				"collapsible-down": {
					from: { height: "0" },
					to: { height: "var(--radix-collapsible-content-height)" },
				},
			},
		},
	},
	plugins: [addVariablesForColors, tailwindAnimate, typography],
} satisfies Config
